import { ImagePlus, RotateCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@marine/ui/button';
import {
  useClassifyPendingImages,
  useConfirmPendingPhoto,
  useDeleteBoatMedia,
} from '@/modules/inventory/hooks/useBoatMedia';
import type { ClassifyPendingResult, PendingBoatImage } from '@/modules/inventory/services/boatMediaApi';
import { AI_PERMISSION_DENIED_MESSAGE } from '@/modules/inventory/hooks/paidAiGuard';
import { deleteImageWithToast } from './deleteImageWithToast';

const CLASSIFY_MESSAGES: Record<ClassifyPendingResult['status'], string> = {
  dispatched: 'Sorting your uploads again…',
  skipped_ai_disabled: 'AI is turned off — use “Use as photo” instead.',
  skipped_no_media: 'There is nothing left to sort.',
  skipped_no_permission: AI_PERMISSION_DENIED_MESSAGE,
};

interface PendingImageActionsProps {
  image: PendingBoatImage;
  boatId: string | null;
  /** Ask before deleting (the boat page, where the gallery beside it asks too). */
  confirmDelete?: boolean;
}

/**
 * #1219 — actions on one held upload: confirm it as a listing photo, or delete
 * it. Shared by the canvas card and the detail-page gallery. Every action needs
 * a boat id. "Sort again" re-runs the whole batch, so it is SortHeldUploadsButton,
 * rendered once per held section.
 */
export function PendingImageActions({ image, boatId, confirmDelete = false }: PendingImageActionsProps) {
  const confirmPhoto = useConfirmPendingPhoto();
  const { mutate: deleteMedia } = useDeleteBoatMedia();

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={!boatId || confirmPhoto.isPending}
        title="Use as photo"
        aria-label={`Use ${image.file_name} as a photo`}
        onClick={(e) => {
          e.stopPropagation();
          if (!boatId) return;
          confirmPhoto.mutate(
            { boatId, mediaId: image.id },
            {
              onSuccess: (outcome) =>
                toast.success(outcome === 'confirmed' ? 'Added to the photos.' : 'This upload was already sorted.'),
              onError: () => toast.error('Could not use this upload as a photo. Please try again.'),
            },
          );
        }}
      >
        <ImagePlus className="size-4" aria-hidden="true" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={!boatId}
        aria-label={`Delete ${image.file_name}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!boatId) return;
          if (confirmDelete && !confirm(`Delete ${image.file_name}? It has not been sorted yet.`)) return;
          deleteImageWithToast(deleteMedia, boatId, image.id);
        }}
      >
        <Trash2 className="size-4 text-destructive" aria-hidden="true" />
      </Button>
    </>
  );
}

/** #1219 — re-run the classifier over the boat's whole intake batch (one button per held section). */
export function SortHeldUploadsButton({ boatId }: { boatId: string | null }) {
  const classify = useClassifyPendingImages();

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={!boatId || classify.isPending}
      onClick={(e) => {
        e.stopPropagation();
        if (!boatId) return;
        classify.mutate(
          { boatId },
          {
            onSuccess: (result) => toast.success(CLASSIFY_MESSAGES[result.status]),
            onError: () => toast.error('Could not start sorting. Please try again.'),
          },
        );
      }}
    >
      <RotateCw className="size-3.5 me-1" aria-hidden="true" />
      Sort again
    </Button>
  );
}
